import React, { useState } from 'react';
import {
  ShieldCheck,
  Lock,
  KeyRound,
  FileCheck,
  Server,
  Activity,
  UserCheck,
  Bug,
  AlertTriangle,
  RefreshCw,
  Mail,
  ChevronRight,
  Shield,
  Clock,
  Sparkles,
  Layers,
  CheckCircle2
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingPage from '../Pages/LandingPage';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Our Commitment', icon: ShieldCheck },
  { id: 'account-security', title: '2. Account Security', icon: Lock },
  { id: 'secure-communication', title: '3. Secure Communication', icon: KeyRound },
  { id: 'data-protection', title: '4. Data Protection & Backups', icon: Server },
  { id: 'monitoring-logging', title: '5. Monitoring & Logging', icon: Activity },
  { id: 'ecosystem-protection', title: '6. Recruiter & Candidate Protection', icon: UserCheck },
  { id: 'responsible-disclosure', title: '7. Responsible Disclosure', icon: Bug },
  { id: 'security-awareness', title: '8. Security Awareness', icon: AlertTriangle },
  { id: 'continuous-improvement', title: '9. Continuous Improvement', icon: RefreshCw },
  { id: 'security-contacts', title: '10. Security Contacts', icon: Mail },
//   { id: 'future-initiatives', title: '11. Future Security Roadmap', icon: Layers },
];

const userSecurityTips = [
  'Use strong, unique passwords with a mix of letters, numbers, and symbols.',
  'Never share your Nexgent credentials or login tokens with anyone.',
  'Verify that you are on the official Nexgent domain before entering passwords.',
  'Keep your local devices and web browsers updated with the latest security patches.',
  'Be cautious of phishing emails or unexpected requests for credentials.',
  'Report any suspected account compromise or unusual behavior immediately.',
];

/* ========================================================================
  FUTURE ROADMAP ITEM DATA STRUCTURE
  ========================================================================
  Uncomment/Use these items when ready to populate the roadmap section dynamically.

  const futureSecurityModules = [
    {
      title: 'Multi-Factor Authentication (MFA) Policy',
      desc: 'Mandatory TOTP / Authenticator app multi-factor challenge for all verified recruiter logins.'
    },
    {
      title: 'Incident Response Policy',
      desc: 'Formally audited 24/7 incident response SLAs and candidate/employer breach notification protocols.'
    },
    {
      title: 'Security Compliance & Certifications',
      desc: 'ISO 27001, SOC 2 Type II, and DPDP / GDPR regulatory data processing compliance audits.'
    },
    {
      title: 'Data Processing & Encryption Standards',
      desc: 'AES-256 field-level encryption for critical personally identifiable information (PII) at rest.'
    },
    {
      title: 'Vulnerability Management Program',
      desc: 'Continuous automated static/dynamic code analysis and bi-annual third-party penetration testing.'
    },
    {
      title: 'Bug Bounty Program',
      desc: 'Public bug bounty rewards program for crowdsourced security vulnerability disclosures.'
    }
  ];
*/

export const SecurityPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                PLATFORM SECURITY & TRUST
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Security at Nexgent
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span>Hardened Enterprise Architecture</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Security Index
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1: Our Commitment to Security */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Our Commitment to Security</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              Nexgent is built to support confidential and highly professional leadership hiring interactions. We recognize that senior candidates and corporate recruiters entrust us with sensitive data, and we take this responsibility with absolute seriousness.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              We continuously protect user accounts, executive profiles, and system infrastructure through technical controls, administrative oversight, and real-time operational monitoring. While no online platform can promise absolute immunity, we strive to implement industry-standard safeguards to maintain security and integrity.
            </p>
          </section>

          {/* Section 2: Account Security */}
          <section id="account-security" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. Account Security & Credential Protection</h2>
            </div>
            
            <div className="space-y-3">
              <h3 className="text-xs font-mono text-cyan-400 uppercase font-semibold">Password Storage Mechanics</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Passwords are <strong>never stored in plain text</strong>. All credentials are processed using strong cryptographic hashing functions prior to persistent storage. This prevents plain-text exposure even in the event of unauthorized access to database backups.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-2">
              <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Authentication Controls</div>
                <div className="text-slate-400">Enforced token session management, automatic expiration, and suspicious attempt throttling.</div>
              </div>
              <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Administrative Safeguards</div>
                <div className="text-slate-400">Strict least-privilege internal system access governed by role-based permissions.</div>
              </div>
            </div>
          </section>

          {/* Section 3: Secure Communication */}
          <section id="secure-communication" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <KeyRound className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Secure Communication & Encryption</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              All communications between your browser or client application and the Nexgent platform are encrypted using standard <strong>HTTPS / TLS protocols</strong>.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Encryption In Transit:</div>
              <p className="text-slate-400 leading-relaxed">
                Transport Layer Security ensures that sensitive credentials, executive profile transmissions, and platform requests are shielded from interception, tampering, or eavesdropping.
              </p>
            </div>
          </section>

          {/* Section 4: Data Protection & Recovery */}
          <section id="data-protection" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Server className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Secure Storage & Continuity</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent implements physical, logical, and environmental controls across its hosting architecture:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-lg text-slate-300">
                <span className="text-cyan-400 font-semibold block mb-0.5">Environment Segregation</span>
                <span className="text-slate-400 text-[11px]">Strict isolation between production networks, staging environments, and internal tools.</span>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-lg text-slate-300">
                <span className="text-cyan-400 font-semibold block mb-0.5">Resilient Data Backups</span>
                <span className="text-slate-400 text-[11px]">Periodic redundant database backups to facilitate disaster recovery and operational continuity.</span>
              </div>
            </div>
          </section>

          {/* Section 5: Monitoring & Logging */}
          <section id="monitoring-logging" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Activity className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Security Monitoring & Activity Audit</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              We employ automated telemetry and audit logging mechanisms to identify anomalous platform behavior:
            </p>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-5">
              <li>Detection of unauthorized login attempts, brute-force patterns, or credential stuffing.</li>
              <li>Monitoring for automated scraping behavior or high-frequency query bursts.</li>
              <li>Secure audit logging for compliance, security investigations, and forensic reviews.</li>
            </ul>
          </section>

          {/* Section 6: Recruiter & Candidate Protection */}
          <section id="ecosystem-protection" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Recruiter & Candidate Protection</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Security on Nexgent extends beyond hardware into procedural checks:
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Vetted Recruiter Verification Workflows</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Candidate Profile Quality & Anonymity Controls</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Permissioned Contact Info Unlocks</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Continuous Platform Behavior Auditing</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Responsible Disclosure Guidelines */}
          <section id="responsible-disclosure" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Bug className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Responsible Vulnerability Disclosure</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              We welcome security researchers and community reports regarding potential vulnerabilities. If you uncover a security flaw, please notify us responsibly before public disclosure.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs font-mono">
              <span className="text-cyan-400 font-semibold uppercase">Security Email: security@nexgent.tech</span>
              <p className="text-slate-400 text-[11px] pt-1">
                Please include a detailed summary, reproduction steps, proof-of-concept details (if available), and your contact details.
              </p>
            </div>
            <div className="p-3 bg-slate-950/60 border border-slate-800/80 rounded-lg text-slate-400 text-xs">
              Researchers are asked to refrain from accessing unauthorized data, disrupting live operations, or engaging in DDoS testing.
            </div>
          </section>

          {/* Section 8: Security Awareness */}
          <section id="security-awareness" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. User Security Hygiene</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Security is a shared responsibility between the platform and network participants:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs pt-1">
              {userSecurityTips.map((tip, idx) => (
                <div key={idx} className="p-3 bg-slate-950/80 border border-slate-800/80 rounded-xl flex items-start gap-2 text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span className="text-[11px] leading-relaxed">{tip}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9: Continuous Improvement */}
          <section id="continuous-improvement" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <RefreshCw className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Continuous Security Evolution</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Cybersecurity threats evolve continually. Nexgent regularly evaluates its application code, cloud architecture, and operational practices to meet emerging threats and maintain a safe leadership recruitment ecosystem.
            </p>
          </section>

          {/* Section 10: Security Contacts */}
          <section id="security-contacts" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Mail className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. Security Team Contacts</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                <div className="text-slate-400 font-mono text-[10px] uppercase">Security Incidents & Vulnerability Reports</div>
                <div className="text-cyan-400 font-mono font-semibold">security@nexgent.tech</div>
              </div>
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                <div className="text-slate-400 font-mono text-[10px] uppercase">General Account Inquiries</div>
                <div className="text-cyan-400 font-mono font-semibold">support@nexgent.tech</div>
              </div>
            </div>

            <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl text-xs space-y-1 font-mono">
              <div className="flex items-center gap-2 text-slate-300">
                <Clock className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Business Operating Hours: Monday – Friday, 9:00 AM – 6:00 PM IST</span>
              </div>
              <p className="text-slate-500 text-[11px] pl-6">
                Response Target for Vulnerability Disclosures: Initial acknowledgment within 2 business days.
              </p>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent Platform Security. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

          {/* Section 11: Additional Sections / Future Roadmap */}
          {/* <section id="future-initiatives" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">11. Additional Future Security Sections</h2>
                <p className="text-xs text-slate-400">Planned security frameworks as the platform scales</p>
              </div>
            </div>

            {/* ========================================================================
              COMMENTED OUT SECTION BELOW
              Uncomment the block below when you are ready to display these live!
              ========================================================================
            */}

            {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
              {futureSecurityModules.map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                  <div className="text-cyan-400 font-semibold text-xs flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            */}

            {/* Placeholder state while roadmap is commented out */}
            {/* <div className="p-4 bg-slate-950/60 border border-slate-800/80 rounded-xl text-center space-y-2">
              <div className="inline-flex p-2 bg-slate-900 text-cyan-400 rounded-lg border border-slate-800">
                <Sparkles className="w-4 h-4" />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed max-w-lg mx-auto">
                Future modules (MFA Policy, Incident Response SLAs, SOC 2 / ISO 27001 Compliance, Field-Level PII Encryption, Vulnerability Management, and Bug Bounty Programs) are maintained in code and ready to activate as scale demands.
              </p>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-slate-500 font-mono mt-4">
              © {new Date().getFullYear()} Motherson Infotech / Nexgent Platform Security. All rights reserved.
            </div>
          </section> */} 

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default SecurityPolicy;